'use strict';

/**
 * Pure view-model for the customer-facing pipeline progress tracker (Level 1 + Level 2).
 *
 * Input: the existing per-node view-models from `buildPipelineStatusGraphViewModel`
 * (`PipelineStatusStage[]`, each optionally tagged with `highLevelStage` from the graph
 * definition). Output: ordered high-level steps with deterministic roll-up states, the
 * current step, and one plain-language message. No React, no fetching — exhaustively
 * unit-testable.
 *
 * Roll-up rules (docs/pizza-tracker/PIPELINE_STATUS_TRACKER_UX_INVESTIGATION.md §9):
 * - failed  ← any tagged child node `failed` (failure dominates everything)
 * - complete ← all tagged child nodes `success`
 * - active  ← any tagged child node left `pending` but not all `success`
 * - pending ← otherwise
 * - `ready` is derived: complete iff every preceding stage is complete; it has no children.
 * - Unknown/malformed facts already fail closed upstream (adapter maps them to `pending`).
 * - Counts come only from existing manifest-backed node counts; no invented percentages.
 * - Delayed: tracker in progress AND the newest bound fact is older than `delayedAfterMs`
 *   (honest: a progressing run keeps emitting facts). `nowMs` is injectable for tests.
 * - Unavailable: pipeline unsupported, or no node has any bound fact at all.
 */

import {
	PIPELINE_PROGRESS_STAGE_IDS,
	PIPELINE_PROGRESS_STAGE_LABELS,
	PIPELINE_PROGRESS_STAGE_MESSAGES,
	PIPELINE_PROGRESS_STAGE_ORDER,
	PIPELINE_PROGRESS_STATE_MESSAGES,
	PIPELINE_PROGRESS_SUB_STEP_LABELS,
	pipelineProgressCountSuffix,
} from './pipelineProgressCopy';
import { formatGraphSpanDurationLabel, parseTimeMs } from './pipelineDuration';

/** Default staleness threshold for the Delayed state (24 h). Configurable per call. */
export const PIPELINE_PROGRESS_DEFAULT_DELAYED_AFTER_MS = 24 * 60 * 60 * 1000;

/** @typedef {'pending' | 'active' | 'complete' | 'failed' | 'delayed'} PipelineProgressStepState */
/** @typedef {'unavailable' | 'inProgress' | 'delayed' | 'failed' | 'ready'} PipelineProgressTrackerState */

/**
 * @typedef {Object} PipelineProgressSubStep
 * @property {string} id graph node id
 * @property {string} label customer-facing sub-step label
 * @property {'pending' | 'active' | 'complete' | 'failed'} state
 * @property {string | undefined} occurredAt raw ISO from the fact (format at render time)
 * @property {{ completed: number, total: number } | undefined} count manifest-backed only
 */

/**
 * @typedef {Object} PipelineProgressStep
 * @property {string} id high-level stage id
 * @property {string} label
 * @property {PipelineProgressStepState} state
 * @property {{ completed: number, total: number } | undefined} count
 * @property {PipelineProgressSubStep[]} subSteps
 */

/**
 * @typedef {Object} PipelineProgressModel
 * @property {boolean} supported
 * @property {PipelineProgressTrackerState} state
 * @property {PipelineProgressStep[]} steps
 * @property {string | undefined} currentStepId first incomplete stage (undefined when ready/unavailable)
 * @property {string} message one plain-language status sentence
 * @property {string | undefined} [durationLabel] overall flow duration for the main ticker
 */

/** @param {string | undefined} iso */
function timeMs(iso) {
	return parseTimeMs(iso);
}

/**
 * Overall flow duration: first valid start → last completion, or now while in progress.
 * `startedAt` counts only when it is earlier than the node span and still before the last completion.
 * @param {import('./pipelineStatusTypes').PipelineStatusStage[]} nodes
 * @param {string | undefined} startedAt
 * @param {PipelineProgressTrackerState} state
 * @param {number} nowMs
 */
function flowDurationLabel(nodes, startedAt, state, nowMs) {
	return formatGraphSpanDurationLabel(nodes, state, nowMs, startedAt);
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusStage} node
 * @returns {'pending' | 'active' | 'complete' | 'failed'}
 */
function subStepStateFromNode(node) {
	switch (node.status) {
		case 'failed':
			return 'failed';
		case 'success':
		case 'skipped':
			return 'complete';
		case 'running':
			return 'active';
		default:
			return 'pending';
	}
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusStage} node
 * @returns {{ completed: number, total: number } | undefined}
 */
function countFromNode(node) {
	if (typeof node.iterationTotalCount !== 'number' || node.iterationTotalCount <= 0) return undefined;
	const completed = typeof node.iterationCompletedCount === 'number' ? node.iterationCompletedCount : 0;
	return { completed, total: node.iterationTotalCount };
}

/**
 * Conservative stage count: common total (max) with the lagging completed count (min).
 * @param {PipelineProgressSubStep[]} subSteps
 */
function rollUpStageCount(subSteps) {
	const counted = subSteps.filter((s) => s.count);
	if (!counted.length) return undefined;
	const total = Math.max(...counted.map((s) => /** @type {{total:number}} */ (s.count).total));
	const completed = Math.min(...counted.map((s) => /** @type {{completed:number}} */ (s.count).completed));
	return { completed, total };
}

/**
 * @param {PipelineProgressSubStep[]} subSteps
 * @returns {PipelineProgressStepState}
 */
function rollUpStageState(subSteps) {
	if (!subSteps.length) return 'pending';
	if (subSteps.some((s) => s.state === 'failed')) return 'failed';
	if (subSteps.every((s) => s.state === 'complete')) return 'complete';
	if (subSteps.some((s) => s.state !== 'pending')) return 'active';
	return 'pending';
}

/**
 * Builds the customer tracker model from graph-node view-models.
 *
 * @param {import('./pipelineStatusTypes').PipelineStatusStage[]} nodes output of `buildPipelineStatusGraphViewModel`
 * @param {{
 *   supported?: boolean,
 *   nowMs?: number,
 *   delayedAfterMs?: number,
 *   startedAt?: string,
 *   stageOrder?: readonly string[],
 *   stageLabels?: Record<string, string>,
 *   stageMessages?: Record<string, string>,
 *   stateMessages?: Record<string, string>,
 * }} [options] `supported=false` forces the unavailable state. The last stage id is the derived Ready step.
 * @returns {PipelineProgressModel}
 */
export function buildPipelineProgressModel(nodes, options) {
	const supported = options?.supported !== false;
	const nowMs = typeof options?.nowMs === 'number' ? options.nowMs : Date.now();
	const delayedAfterMs =
		typeof options?.delayedAfterMs === 'number' && options.delayedAfterMs > 0
			? options.delayedAfterMs
			: PIPELINE_PROGRESS_DEFAULT_DELAYED_AFTER_MS;
	const stageOrder = Array.isArray(options?.stageOrder) && options.stageOrder.length
		? options.stageOrder
		: PIPELINE_PROGRESS_STAGE_ORDER;
	const stageLabels = options?.stageLabels ?? PIPELINE_PROGRESS_STAGE_LABELS;
	const stageMessages = options?.stageMessages ?? PIPELINE_PROGRESS_STAGE_MESSAGES;
	const stateMessages = options?.stateMessages ?? PIPELINE_PROGRESS_STATE_MESSAGES;
	const startedAt = options?.startedAt;

	const safeNodes = Array.isArray(nodes) ? nodes.filter((n) => n && typeof n === 'object') : [];

	/** @type {PipelineProgressStep[]} */
	const steps = stageOrder.map((stageId) => {
		const children = safeNodes.filter(
			(n) => typeof n.highLevelStage === 'string' && n.highLevelStage === stageId,
		);
		const subSteps = children.map((n) => ({
			id: n.id,
			label: PIPELINE_PROGRESS_SUB_STEP_LABELS[n.id] || n.description || n.id,
			state: subStepStateFromNode(n),
			occurredAt: n.occurredAt,
			count: countFromNode(n),
		}));
		return {
			id: stageId,
			label: stageLabels[stageId] || stageId,
			state: rollUpStageState(subSteps),
			count: rollUpStageCount(subSteps),
			subSteps,
		};
	});

	// The last stage is derived Ready; it owns no nodes.
	const readyStageId = stageOrder[stageOrder.length - 1];
	const readyIndex = steps.findIndex((s) => s.id === readyStageId);
	if (readyIndex >= 0) {
		const priorComplete = steps
			.filter((_, i) => i !== readyIndex)
			.every((s) => s.state === 'complete');
		steps[readyIndex] = {
			...steps[readyIndex],
			state: priorComplete ? 'complete' : 'pending',
			subSteps: [],
			count: undefined,
		};
	}

	const anyBoundFact = safeNodes.some((n) => n.factId != null && String(n.factId).trim() !== '');
	if (!supported || !anyBoundFact) {
		return {
			supported,
			state: 'unavailable',
			steps,
			currentStepId: undefined,
			message: stateMessages.unavailable,
			durationLabel: undefined,
		};
	}

	const anyFailed = steps.some((s) => s.state === 'failed');
	const allComplete = steps.every((s) => s.state === 'complete');
	const firstIncomplete = steps.find((s) => s.state !== 'complete');
	const currentStepId = allComplete ? undefined : firstIncomplete?.id;

	if (anyFailed) {
		const state = /** @type {const} */ ('failed');
		return {
			supported,
			state,
			steps,
			currentStepId: steps.find((s) => s.state === 'failed')?.id,
			message: stateMessages.failed,
			durationLabel: flowDurationLabel(safeNodes, startedAt, state, nowMs),
		};
	}

	if (allComplete) {
		const state = /** @type {const} */ ('ready');
		return {
			supported,
			state,
			steps,
			currentStepId: undefined,
			message: stateMessages.ready,
			durationLabel: flowDurationLabel(safeNodes, startedAt, state, nowMs),
		};
	}

	// Delayed: run is in progress but the newest bound fact is stale.
	const newestFactMs = Math.max(
		Number.NEGATIVE_INFINITY,
		...safeNodes.map((n) => timeMs(n.occurredAt) ?? Number.NEGATIVE_INFINITY),
	);
	const delayed = Number.isFinite(newestFactMs) && nowMs - newestFactMs > delayedAfterMs;
	if (delayed && currentStepId) {
		const marked = steps.map((s) => (s.id === currentStepId ? { ...s, state: /** @type {PipelineProgressStepState} */ ('delayed') } : s));
		const state = /** @type {const} */ ('delayed');
		return {
			supported,
			state,
			steps: marked,
			currentStepId,
			message: stateMessages.delayed,
			durationLabel: flowDurationLabel(safeNodes, startedAt, 'inProgress', nowMs),
		};
	}

	const current = steps.find((s) => s.id === currentStepId);
	let message = stageMessages[currentStepId] || stageMessages[stageOrder[0]] || PIPELINE_PROGRESS_STAGE_MESSAGES[PIPELINE_PROGRESS_STAGE_IDS.received];
	if (current?.count) {
		message = message.replace(/\.$/, '') + pipelineProgressCountSuffix(current.count.completed, current.count.total);
	}

	return {
		supported,
		state: 'inProgress',
		steps,
		currentStepId,
		message,
		durationLabel: flowDurationLabel(safeNodes, startedAt, 'inProgress', nowMs),
	};
}
